import createClient from 'openapi-fetch';
import { createConnectedWalletClient, } from './connected-wallet/connected-wallet-client.js';
import { createCryptoDepositClient, } from './crypto-deposit/crypto-deposit-client.js';
import { isFundingMethod } from './discovery/policy.js';
import { createExchangeClient } from './exchange/exchange-client.js';
import { createIntentClient } from './intent-client.js';
import { createWithdrawalClient } from './offramp/client.js';
import { createFundingTelemetry, } from './operations/funnel-telemetry.js';
export function isDepositOperation(operation) {
    return !operation.withdrawal && isFundingMethod(operation.method);
}
export class OnrampApiError extends Error {
    status;
    code;
    requestId;
    retryAfterMs;
    recovery;
    reason;
    constructor(status, code, requestId, retryAfterMs = 0, recovery, reason) {
        super(code);
        this.status = status;
        this.code = code;
        this.requestId = requestId;
        this.retryAfterMs = retryAfterMs;
        this.recovery = recovery;
        this.reason = reason;
        this.name = 'OnrampApiError';
    }
}
let clientSequence = 0;
/** Auth is injected by the host; no backend SDK or persisted provider credentials. */
export function createOnrampClient(options) {
    const base = new URL(options.baseUrl);
    if (base.protocol !== 'https:' &&
        !(base.protocol === 'http:' &&
            (['localhost', '127.0.0.1', '[::1]'].includes(base.hostname) ||
                base.hostname.endsWith('.localhost')))) {
        throw new Error('onramp_requires_secure_origin');
    }
    const telemetry = createFundingTelemetry(options.telemetry);
    const lifecycle = (event) => {
        try {
            options.telemetry?.onLifecycle?.(event);
        }
        catch {
            options.telemetry?.onFailure?.('onramp_telemetry_failed');
        }
    };
    const active = new Set();
    let disposed = false;
    let sessionGeneration = 0;
    const disposeListeners = new Set();
    const assertSession = (generation) => {
        if (disposed)
            throw new Error('onramp_session_disposed');
        if (generation !== sessionGeneration)
            throw new Error('onramp_session_disconnected');
    };
    const transport = createClient({
        baseUrl: options.baseUrl,
        credentials: 'include',
        fetch: async (request) => {
            if (disposed)
                throw new Error('onramp_session_disposed');
            const generation = sessionGeneration;
            const controller = new AbortController();
            const abort = () => controller.abort();
            request.signal.addEventListener('abort', abort, { once: true });
            if (request.signal.aborted)
                controller.abort();
            active.add(controller);
            try {
                const headers = new Headers(await options.getAuthHeaders());
                if (disposed || controller.signal.aborted)
                    throw new Error('onramp_session_disposed');
                for (const [name, value] of request.headers)
                    headers.set(name, value);
                const response = await (options.fetch ?? fetch)(new Request(request, { headers, signal: controller.signal }));
                if (generation !== sessionGeneration)
                    throw new Error('onramp_session_disconnected');
                return response;
            }
            finally {
                active.delete(controller);
                request.signal.removeEventListener('abort', abort);
            }
        },
    });
    return {
        connectedWallet: createConnectedWalletClient(transport),
        cryptoDeposit: createCryptoDepositClient(transport),
        exchange: createExchangeClient(transport),
        ...createWithdrawalClient(transport),
        ...createIntentClient(transport, () => {
            if (disposed)
                throw new Error('onramp_session_disposed');
        }),
        cacheKey: `${Date.now()}-${++clientSequence}`,
        ...(options.progressKey ? { progressKey: `${options.baseUrl}:${options.progressKey}` } : {}),
        observeDiagnostic: telemetry.diagnose,
        observeFunnel: telemetry.observe,
        observeLifecycle: lifecycle,
        onDispose(listener) {
            if (disposed)
                listener();
            else
                disposeListeners.add(listener);
            return () => {
                disposeListeners.delete(listener);
            };
        },
        async disconnectSession(signal) {
            const generation = sessionGeneration;
            const result = await transport.DELETE('/v1/onramp/sessions', {
                ...(signal ? { signal } : {}),
            });
            assertSession(generation);
            if (result.response.status !== 204)
                throw new OnrampApiError(result.response.status, result.error?.error.code ?? 'onramp_request_failed', result.error?.requestId, retryDelay(result.response));
            lifecycle({ event: 'disconnected', reason: 'explicit_disconnect' });
            sessionGeneration += 1;
            for (const controller of active)
                controller.abort();
        },
        async createSession(signal) {
            const generation = sessionGeneration;
            const result = await transport.POST('/v1/onramp/sessions', { ...(signal ? { signal } : {}) });
            assertSession(generation);
            if (result.data) {
                lifecycle({ event: 'session_created', reason: 'initial' });
                return result.data.data;
            }
            throw new OnrampApiError(result.response.status, result.error?.error.code ?? 'onramp_request_failed', result.error?.requestId, retryDelay(result.response));
        },
        async createAuthenticationSession(email, signal, acceptedTermsVersion) {
            const generation = sessionGeneration;
            const result = await transport.POST('/v1/onramp/sessions', {
                body: {
                    ...(email === undefined ? {} : { authenticationEmail: email }),
                    ...(acceptedTermsVersion === undefined ? {} : { acceptedTermsVersion }),
                },
                ...(signal ? { signal } : {}),
            });
            assertSession(generation);
            if (result.data) {
                lifecycle({
                    event: 'session_created',
                    reason: acceptedTermsVersion ? 'terms' : 'authentication',
                });
                return result.data.data;
            }
            throw new OnrampApiError(result.response.status, result.error?.error.code ?? 'onramp_request_failed', result.error?.requestId, retryDelay(result.response));
        },
        async startPhoneVerification(phoneNumber, signal) {
            const generation = sessionGeneration;
            const result = await transport.POST('/v1/onramp/phone/verifications', {
                body: { phoneNumber },
                ...(signal ? { signal } : {}),
            });
            assertSession(generation);
            if (result.data)
                return result.data.data;
            throw new OnrampApiError(result.response.status, result.error?.error.code ?? 'onramp_request_failed', result.error?.requestId, retryDelay(result.response));
        },
        async checkPhoneVerification(id, code, signal) {
            const generation = sessionGeneration;
            const result = await transport.POST('/v1/onramp/phone/verifications/{id}/check', {
                params: { path: { id } },
                body: { code },
                ...(signal ? { signal } : {}),
            });
            assertSession(generation);
            if (result.data)
                return result.data.data;
            throw new OnrampApiError(result.response.status, result.error?.error.code ?? 'onramp_request_failed', result.error?.requestId, retryDelay(result.response));
        },
        async startEmailVerification(email, signal) {
            const generation = sessionGeneration;
            const result = await transport.POST('/v1/onramp/email/verifications', {
                body: { email },
                ...(signal ? { signal } : {}),
            });
            assertSession(generation);
            if (result.data)
                return result.data.data;
            throw new OnrampApiError(result.response.status, result.error?.error.code ?? 'onramp_request_failed', result.error?.requestId, retryDelay(result.response));
        },
        async checkEmailVerification(id, code, signal) {
            const generation = sessionGeneration;
            const result = await transport.POST('/v1/onramp/email/verifications/{id}/check', {
                params: { path: { id } },
                body: { code },
                ...(signal ? { signal } : {}),
            });
            assertSession(generation);
            if (result.data)
                return result.data.data;
            throw new OnrampApiError(result.response.status, result.error?.error.code ?? 'onramp_request_failed', result.error?.requestId, retryDelay(result.response));
        },
        async getVerifiedPhone(signal) {
            const generation = sessionGeneration;
            const result = await transport.GET('/v1/onramp/payer/verified-phone', {
                cache: 'no-store',
                ...(signal ? { signal } : {}),
            });
            assertSession(generation);
            if (result.data)
                return result.data.data;
            throw new OnrampApiError(result.response.status, result.error?.error.code ?? 'onramp_request_failed', result.error?.requestId, retryDelay(result.response));
        },
        async getPayerStatus(signal) {
            const generation = sessionGeneration;
            const result = await transport.GET('/v1/onramp/payer', { ...(signal ? { signal } : {}) });
            assertSession(generation);
            if (result.data)
                return result.data.data;
            throw new OnrampApiError(result.response.status, result.error?.error.code ?? 'onramp_request_failed', result.error?.requestId, retryDelay(result.response));
        },
        async acceptConsent(version, signal) {
            const generation = sessionGeneration;
            const result = await transport.POST('/v1/onramp/payer/consent', {
                body: { version },
                ...(signal ? { signal } : {}),
            });
            assertSession(generation);
            if (result.data)
                return result.data.data;
            throw new OnrampApiError(result.response.status, result.error?.error.code ?? 'onramp_request_failed', result.error?.requestId, retryDelay(result.response));
        },
        async getRealtimeCredentials(signal) {
            const generation = sessionGeneration;
            const result = await transport.POST('/v1/auth/nats-credentials', {
                ...(signal ? { signal } : {}),
            });
            assertSession(generation);
            if (result.data?.kind === 'user')
                return result.data;
            throw new OnrampApiError(result.response.status, 'onramp_realtime_unavailable', undefined, retryDelay(result.response));
        },
        async getHistory(query = {}, signal) {
            const generation = sessionGeneration;
            const result = await transport.GET('/v1/onramp/history', {
                params: { query },
                ...(signal ? { signal } : {}),
            });
            assertSession(generation);
            if (result.data)
                return result.data;
            throw new OnrampApiError(result.response.status, result.error?.error.code ?? 'onramp_request_failed', result.error?.requestId, retryDelay(result.response));
        },
        async hideHistoryOperation(id) {
            const generation = sessionGeneration;
            const result = await transport.POST('/v1/onramp/operations/{id}/hide', {
                params: { path: { id } },
            });
            assertSession(generation);
            if (result.data?.data.id === id && result.data.data.hidden === true)
                return;
            throw new OnrampApiError(result.response.status, result.error?.error.code ?? 'onramp_history_hide_failed', result.error?.requestId, retryDelay(result.response));
        },
        async getOperation(id, signal) {
            const generation = sessionGeneration;
            const result = await transport.GET('/v1/onramp/operations/{id}', {
                params: { path: { id } },
                ...(signal ? { signal } : {}),
            });
            assertSession(generation);
            if (result.data)
                return result.data;
            throw new OnrampApiError(result.response.status, result.error?.error.code ?? 'onramp_request_failed', result.error?.requestId, retryDelay(result.response));
        },
        async getBankTransferInstructions(id, signal) {
            const generation = sessionGeneration;
            const result = await transport.GET('/v1/onramp/operations/{id}/bank-transfer-instructions', {
                params: { path: { id } },
                cache: 'no-store',
                ...(signal ? { signal } : {}),
            });
            assertSession(generation);
            if (result.data)
                return result.data.data;
            throw new OnrampApiError(result.response.status, 'onramp_bank_instructions_unavailable', undefined);
        },
        dispose() {
            if (disposed)
                return;
            lifecycle({ event: 'client_disposed', reason: 'owner_scope_unmounted' });
            disposed = true;
            telemetry.dispose();
            for (const controller of active)
                controller.abort();
            active.clear();
            for (const listener of disposeListeners)
                listener();
            disposeListeners.clear();
        },
    };
}
function retryDelay(response) {
    const value = response.headers.get('Retry-After');
    if (!value)
        return 0;
    const delay = /^\d+$/.test(value) ? Number(value) * 1000 : Date.parse(value) - Date.now();
    return Number.isFinite(delay) ? Math.min(2147483647, Math.max(0, delay)) : 0;
}
